import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  get,
} from "react-hook-form";
import React, { type ReactNode } from "react";

import {
  NumberInput,
  type NumberInputProps,
} from "src/components/NumberInput/NumberInput.tsx";
import type { ValueType } from "rc-input-number";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";

export type NumberFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  forceValue?: ValueType | null;
  disabledTooltip?: ReactNode;
} & NumberInputProps &
  Omit<ControllerProps<TFieldValues, TName>, "render">;

// todo: forward ref
export const NumberField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: NumberFieldProps<TFieldValues, TName>,
) => {
  const {
    name,
    control,
    disabled,
    shouldUnregister,
    rules,
    defaultValue,
    precision = 6,
    controls = true,
    disabledTooltip,
    onChange: onChangeProp,
    forceValue,
    onBlur: onBlurProp,
    ...restProps
  } = props;

  return (
    <Controller
      key={name}
      rules={rules}
      name={name}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      control={control}
      render={({
        field: { name, value, onChange, onBlur, ref },
        formState: { errors },
      }) => {
        return (
          <TooltipV2
            style={{
              width: "100%",
            }}
            disabled={!disabledTooltip}
            content={disabledTooltip}
          >
            <NumberInput
              ref={ref}
              name={name}
              disabled={disabled}
              controls={controls}
              onChange={(value) => {
                onChange(value);
                onChangeProp?.(value);
              }}
              precision={precision}
              error={
                get(errors?.root || {}, name)?.message // server error
              }
              onBlur={(event) => {
                onBlur();
                onBlurProp?.(event);
              }}
              value={forceValue != null ? forceValue : value}
              {...restProps}
            />
          </TooltipV2>
        );
      }}
    />
  );
};
